import React, { useMemo, useState, useEffect } from 'react';
import { api } from '../api';
import { useI18n } from '../i18n/context';
import { getAuditLabel } from '../utils/auditLabels';
import ListToolbar from '../ui/ListToolbar';
import { loadState, persistState, resetState } from '../utils/statePersistence';
import '../styles/ui.css';
import './ListPages.css';

export default function Audit() {
  const { t, formatDateTime } = useI18n();
  const defaults = { page: 1, actionFilter: '', actorFilter: '', startDate: '', endDate: '' };
  const { state: persisted } = loadState('sa-audit-state', defaults, {
    page: { param: 'page', parse: (v) => Math.max(1, parseInt(v, 10) || 1), serialize: (v) => String(v) },
    actionFilter: { param: 'action' },
    actorFilter: { param: 'actor' },
    startDate: { param: 'from' },
    endDate: { param: 'to' }
  });

  const [data, setData] = useState({ data: [], pagination: { page: persisted.page, limit: 50, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(persisted.page);
  const [actionFilter, setActionFilter] = useState(persisted.actionFilter);
  const [actorFilter, setActorFilter] = useState(persisted.actorFilter);
  const [startDate, setStartDate] = useState(persisted.startDate);
  const [endDate, setEndDate] = useState(persisted.endDate);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    persistState(
      'sa-audit-state',
      { page, actionFilter, actorFilter, startDate, endDate },
      defaults,
      {
        page: { param: 'page', serialize: (v) => String(v) },
        actionFilter: { param: 'action' },
        actorFilter: { param: 'actor' },
        startDate: { param: 'from' },
        endDate: { param: 'to' }
      }
    );
  }, [page, actionFilter, actorFilter, startDate, endDate]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 50 });
    if (actionFilter) params.set('action', actionFilter);
    if (startDate) params.set('startDate', startDate);
    if (endDate) params.set('endDate', endDate);
    api(`/superadmin/audit?${params}`)
      .then(setData)
      .catch(() => setData({ data: [], pagination: { page: 1, limit: 50, total: 0 } }))
      .finally(() => setLoading(false));
  }, [page, actionFilter, startDate, endDate]);

  const { data: list, pagination } = data;
  const totalPages = Math.max(1, Math.ceil((pagination.total || 0) / (pagination.limit || 50)));
  const filtered = useMemo(() => {
    if (!actorFilter) return list;
    const q = actorFilter.trim().toLowerCase();
    if (!q) return list;
    return list.filter((row) => {
      const actor = `${row.actorEmail || ''} ${row.actorName || ''} ${row.actorId || ''}`.toLowerCase();
      return actor.includes(q);
    });
  }, [list, actorFilter]);

  function parseMeta(metadata) {
    if (!metadata) return null;
    try {
      return typeof metadata === 'string' ? JSON.parse(metadata) : metadata;
    } catch (_) {
      return { raw: metadata };
    }
  }

  function resultBadgeVariant(result) {
    if (result === 'ok') return 'success';
    if (result === 'warning') return 'warning';
    return 'error';
  }

  function ActionIcon({ action }) {
    const a = String(action || '');
    if (a.includes('login')) {
      return (
        <svg viewBox="0 0 24 24" className="sa-audit-icon" aria-hidden="true">
          <path d="M12 3l8 3v6c0 5-3.4 8.7-8 10-4.6-1.3-8-5-8-10V6l8-3z" />
        </svg>
      );
    }
    if (a.includes('dashboard_view')) {
      return (
        <svg viewBox="0 0 24 24" className="sa-audit-icon" aria-hidden="true">
          <path d="M12 5c5 0 9 4 9 7s-4 7-9 7-9-4-9-7 4-7 9-7zm0 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" />
        </svg>
      );
    }
    if (a.includes('organization')) {
      return (
        <svg viewBox="0 0 24 24" className="sa-audit-icon" aria-hidden="true">
          <path d="M3 10h8V4H3v6zm10 10h8V4h-8v16zM3 20h8v-8H3v8z" />
        </svg>
      );
    }
    if (a.includes('user')) {
      return (
        <svg viewBox="0 0 24 24" className="sa-audit-icon" aria-hidden="true">
          <path d="M7.5 12a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm9 3a3.5 3.5 0 1 1 0-7 3.5 3.5 0 0 1 0 7zM3 20a4.5 4.5 0 0 1 9 0v1H3v-1zm11-1a4 4 0 0 1 8 0v1h-8v-1z" />
        </svg>
      );
    }
    if (a.includes('delete')) {
      return (
        <svg viewBox="0 0 24 24" className="sa-audit-icon" aria-hidden="true">
          <path d="M6 7h12l-1 14H7L6 7zm3-3h6l1 2H8l1-2z" />
        </svg>
      );
    }
    if (a.includes('password') || a.includes('security')) {
      return (
        <svg viewBox="0 0 24 24" className="sa-audit-icon" aria-hidden="true">
          <path d="M6 10h12v10H6V10zm2-2V6a4 4 0 1 1 8 0v2h-2V6a2 2 0 1 0-4 0v2H8z" />
        </svg>
      );
    }
    return (
      <svg viewBox="0 0 24 24" className="sa-audit-icon" aria-hidden="true">
        <path d="M4 3h10l6 6v12H4zM13 3v6h6M7 13h10v2H7zm0 4h10v2H7z" />
      </svg>
    );
  }

  function handleClearFilters() {
    const next = resetState('sa-audit-state', defaults);
    setActionFilter(next.actionFilter);
    setActorFilter(next.actorFilter);
    setStartDate(next.startDate);
    setEndDate(next.endDate);
    setPage(next.page);
  }

  function exportCsv() {
    if (!filtered.length) return;
    const headers = ['date', 'actor', 'action', 'resource', 'ip', 'result'];
    const rows = filtered.map((row) => ([
      row.createdAt || '',
      row.actorEmail || row.actorName || row.actorId || '',
      row.action || '',
      row.resourceType && row.resourceId ? `${row.resourceType}:${row.resourceId}` : '',
      row.ip || '',
      row.result || ''
    ]));
    const escapeCell = (cell) => `"${String(cell).replace(/"/g, '""')}"`;
    const csv = [headers.join(','), ...rows.map((r) => r.map(escapeCell).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'audit-log.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="sa-page-frame">
      <div className="sa-page-header">
        <h1 className="sa-page-title">{t('audit.title')}</h1>
        <p className="sa-page-subtitle">{t('audit.subtitle')}</p>
      </div>
      <div className="ui-page-toolbar">
        <ListToolbar
          title={null}
          subtitle={null}
          filters={[
            <input
              key="actor"
              type="text"
              className="ui-input"
              placeholder={t('audit.actorFilterPlaceholder')}
              value={actorFilter}
              onChange={(e) => { setActorFilter(e.target.value); setPage(1); }}
            />,
            <input
              key="action"
              type="text"
              className="ui-input"
              placeholder={t('audit.actionFilterPlaceholder')}
              value={actionFilter}
              onChange={(e) => { setActionFilter(e.target.value); setPage(1); }}
            />,
            <input
              key="from"
              type="date"
              className="ui-input"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            />,
            <input
              key="to"
              type="date"
              className="ui-input"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            />,
            <button key="clear" type="button" className="ui-button ghost" onClick={handleClearFilters}>
              {t('audit.clearFilters')}
            </button>
          ]}
          actions={(
            <button type="button" className="ui-button primary" onClick={exportCsv}>
              {t('audit.exportCsv')}
            </button>
          )}
        />
      </div>
      <div className="ui-page-content">
        <div className="sa-card sa-audit-list-card">
        {loading ? (
          <div className="sa-loading">{t('common.loading')}</div>
        ) : filtered.length === 0 ? (
          <div className="sa-empty">{t('audit.empty')}</div>
        ) : (
          <>
            <div className="sa-audit-list">
              {filtered.map((row) => {
                const meta = parseMeta(row.metadata);
                const hasMeta = meta && Object.keys(meta).length > 0;
                const isExpanded = expandedId === row.id;
                return (
                  <div key={row.id} className="sa-audit-item">
                    <div className="sa-audit-icon-wrap">
                      <ActionIcon action={row.action} />
                    </div>
                    <div className="sa-audit-content">
                      <div className="sa-audit-header">
                        <div>
                          <div className="sa-audit-action">{getAuditLabel(row.action, t)}</div>
                          <div className="sa-audit-actor">{row.actorEmail || row.actorName || row.actorId || '—'}</div>
                          <div className="sa-audit-meta-row">
                            <span>{row.createdAt ? formatDateTime(row.createdAt) : '—'}</span>
                            <span>·</span>
                            <span className="sa-audit-ip">{row.ip || '—'}</span>
                          </div>
                        </div>
                        <div className="sa-audit-badges">
                          <span className={`sa-badge sa-badge--${resultBadgeVariant(row.result)}`}>
                            {row.result === 'ok' ? t('audit.resultOk') : row.result === 'warning' ? t('audit.resultWarning') : t('audit.resultError')}
                          </span>
                          {hasMeta && (
                            <button
                              type="button"
                              className="sa-audit-expand"
                              onClick={() => setExpandedId(isExpanded ? null : row.id)}
                              aria-expanded={isExpanded}
                            >
                              {isExpanded ? '▾' : '▸'}
                            </button>
                          )}
                        </div>
                      </div>
                      {isExpanded && hasMeta && (
                        <div className="sa-audit-detail-panel">
                          <div className="sa-audit-meta">
                            {meta.before && (
                              <div><strong>{t('audit.before')}:</strong> <pre className="sa-code">{JSON.stringify(meta.before, null, 2)}</pre></div>
                            )}
                            {meta.after && (
                              <div><strong>{t('audit.after')}:</strong> <pre className="sa-code">{JSON.stringify(meta.after, null, 2)}</pre></div>
                            )}
                            {!meta.before && !meta.after && <pre className="sa-code">{JSON.stringify(meta, null, 2)}</pre>}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {totalPages > 1 && (
              <div className="sa-pagination">
                <button type="button" className="sa-btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('common.previous')}</button>
                <span className="sa-pagination-info">{t('common.page')} {page} {t('common.of')} {totalPages} · {pagination.total} {t('common.results')}</span>
                <button type="button" className="sa-btn-secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>{t('common.next')}</button>
              </div>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
}
